import {createSlice, Draft, PayloadAction} from '@reduxjs/toolkit'
import {anchorReceived, AnchorReceivedPayload} from 'modules/sapiSearch/slice'
import {syncStoreWithUrl, SyncStoreWithUrlPayload} from 'modules/search/slice'
import {defaultTo} from 'ramda'
import {SearchParamsType} from 'types/Search'
import {
  getWtValueFromLabelParam,
  hasDefaultDates as hasMetaDefaultDates
} from 'utils/trafficSource'

import {userSuccessfullyAuthenticated} from '@vio/core/src/auth/modules/actions'
import {DEFAULT_ROOMS} from '@vio/core/src/room/business/roomConfiguration/config'
import {SapiResultSearchParameters} from '@vio/core/src/sapi/types'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'

import {
  DatePickerType,
  OpenState,
  SearchBoxAncestor,
  SearchBoxStateType,
  SearchBoxValues,
  SearchTrigger,
  SetSuggestionsPayload
} from './types'
import {getValidSearchBoxValues} from './utils'

import type {SearchParameters} from '@findhotel/sapi'

const initialStateValues: SearchBoxValues = {
  rooms: DEFAULT_ROOMS,
  optimizeRooms: '1'
}

export const initialState: SearchBoxStateType = {
  ancestor: null,
  searchBoxDestinationDisplayValue: null,
  isDefaultDatesMessageVisible: null,
  isDatesVisible: true,
  isAnchorHotelUnavailable: null,
  openState: OpenState.Unset,
  isSearchBoxActive: false,
  openDatePickerType: null,
  values: initialStateValues,
  suggestions: [],
  searchTrigger: SearchTrigger.Submit,
  suggestionsSearchValue: '',
  isUserChangedDates: false
}

const DESTINATION_URL_PARAMS = [
  'hotelId',
  'placeId',
  'address',
  'userLocationSearch',
  'boundingBox',
  'iataCode'
] as const

const hasDestinationUrlParam = (
  urlParams: SyncStoreWithUrlPayload['urlParams']
): boolean => DESTINATION_URL_PARAMS.some(param => Boolean(urlParams[param]))

const SEARCH_TRIGGERS_THAT_CLOSE_SEARCH_BOX = [
  SearchTrigger.RecentSearches,
  SearchTrigger.ContinueSearchWidget,
  SearchTrigger.ExtendYourStayWidget,
  SearchTrigger.TopDealsTonightWidget
]

/**
 * Gets the open state of the search box
 * @param state - The SearchBox state
 * @param payload - The payload from the `syncStoreWithUrl` side effect
 * @returns The SearchBox `OpenState`
 */
const getOpenState = (
  state: SearchBoxStateType,
  urlParams: SyncStoreWithUrlPayload['urlParams'],
  isSearchBoxOpenByDefault: SyncStoreWithUrlPayload['isSearchBoxOpenByDefault'],
  dateValidationReason?: DateValidationReason | null,
  isMobile?: boolean
): OpenState => {
  const isUserSearch = urlParams?.userSearch === '1'
  // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
  const hasBookingHint = urlParams?.bookingHint === '1'
  // Widget landings (identified by `wt` in the label) already carry user intent
  // for the given itinerary — suppress the mobile auto-open/highlight so users
  // see the results directly. Desktop tooltip behavior (driven by
  // isDefaultDatesMessageVisible) is unaffected.
  const isMobileWidgetLanding =
    Boolean(isMobile) &&
    Boolean(getWtValueFromLabelParam(urlParams as SearchParamsType))

  if (
    !isMobileWidgetLanding &&
    (urlParams.openSearchBox ||
      (hasBookingHint &&
        !dateValidationReason &&
        state.openState === OpenState.Unset))
  ) {
    return OpenState.OpenByDefault
  }

  // Close searchbox when user comes from any home page widget —
  // unless bookingHint=1 explicitly asked to keep it open (mobile).
  // Without this guard, a second syncStoreWithUrl after the first (e.g. when
  // the auth state resolves) would clobber the bookingHint-driven OpenByDefault
  // with Closed, leaving the sticky isSearchBoxActive overlay visible but no form.
  if (
    !hasBookingHint &&
    SEARCH_TRIGGERS_THAT_CLOSE_SEARCH_BOX.includes(state.searchTrigger)
  ) {
    return OpenState.Closed
  }

  // TODO: (@search) - relook openstate logic once we clean up 15b632cb-rebrand-search
  // This is a hack so that we never set the searchbox to open on the home page by default which was
  // causing the searchbox to be OPEN_BY_DEFAULT when navigating to SRP
  if (!hasDestinationUrlParam(urlParams)) {
    return OpenState.Unset
  }

  // SearchTrigger is set when triggering a search
  // We use it to determine whether the searchbox should be open afterwards
  const userHasOpenedSearchBox = [
    SearchTrigger.DatePicker,
    SearchTrigger.GuestPicker,
    SearchTrigger.Suggestions
  ].includes(state.searchTrigger)
  if (userHasOpenedSearchBox) return OpenState.OpenByUser

  // Initially set to open but should be closed if it is a user search
  if (
    state.openState === OpenState.OpenByDefault &&
    isUserSearch &&
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    !hasBookingHint
  )
    return OpenState.Closed

  // Retain already initialized state
  if (state.openState !== OpenState.Unset) return state.openState

  // Determine initial/fallback state
  if (isSearchBoxOpenByDefault && !isMobileWidgetLanding) {
    return OpenState.OpenByDefault
  }
  return OpenState.Unset
}

/**
 * Returns if the default dates message is visible
 */
const getDefaultDatesMessageVisibility = (
  isDefaultDatesMessageVisible: boolean,
  isSearchBoxOpenByDefault: boolean
): boolean =>
  defaultTo(
    false,
    defaultTo(isSearchBoxOpenByDefault, isDefaultDatesMessageVisible)
  )

/**
 * Applies the search box values to the state
 * @param state - SearchBox state
 * @param values - SearchBox values
 */
const applySearchBoxValues = (
  state: Draft<SearchBoxStateType>,
  values: SearchBoxValues
) => {
  const searchBoxValues = getValidSearchBoxValues(values)
  const changedKeys = Object.keys(searchBoxValues)
  const isDatesChange =
    changedKeys.includes('checkIn') || changedKeys.includes('checkOut')
  if (isDatesChange) {
    state.isDatesVisible = true
    state.isDefaultDatesMessageVisible = false
  }

  if (state.isSearchBoxActive && isDatesChange) state.isUserChangedDates = true

  state.values = {
    ...state.values,
    ...searchBoxValues
  }
}

/*
 * Extracts SearchBox values from SAPI search parameters
 */
const extractSearchBoxValues = (
  searchParameters: SapiResultSearchParameters
): SearchBoxValues => {
  return {
    boundingBox: searchParameters.boundingBox,
    checkIn: searchParameters.checkIn,
    checkOut: searchParameters.checkOut,
    hotelId: searchParameters.hotelId,
    placeId: searchParameters.placeId,
    rooms: searchParameters.rooms,
    address: searchParameters.address,
    iataCode: searchParameters.iataCode,
    optimizeRooms: searchParameters.optimizeRooms ? 1 : undefined,
    // If geolocation is set, it means that we are doing a user location search. This should be updated in the search box.
    userLocationSearch:
      searchParameters.geolocation !== undefined ? 1 : undefined
  }
}

export const DEFAULT_ANCESTOR = SearchBoxAncestor.Page

const {actions, reducer} = createSlice({
  name: 'searchBox',
  initialState,
  reducers: {
    openSearchBox: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<SearchBoxAncestor>
    ) => {
      state.ancestor = payload
      state.openState = OpenState.OpenByUser
      if (SEARCH_TRIGGERS_THAT_CLOSE_SEARCH_BOX.includes(state.searchTrigger)) {
        state.searchTrigger = initialState.searchTrigger
      }
    },
    closeSearchBox: (state: SearchBoxStateType) => {
      state.ancestor = null
      state.openState = OpenState.Closed
      state.isDefaultDatesMessageVisible = false
      state.searchTrigger = SearchTrigger.Close
      state.isSearchBoxActive = false
    },
    setSearchBoxValue: (
      state: SearchBoxStateType,
      {
        payload
      }: PayloadAction<{
        name: string
        value: unknown
      }>
    ) => {
      const isDatesChange = ['checkIn', 'checkOut'].includes(payload.name)
      if (isDatesChange) {
        state.isDatesVisible = true
        state.isDefaultDatesMessageVisible = false
      }

      state.values[payload.name] = payload.value
    },
    setSearchBoxValues: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<SearchBoxValues>
    ) => {
      applySearchBoxValues(state, payload)
    },
    setLandingGuestConfig: (
      state: SearchBoxStateType,
      {
        payload
      }: PayloadAction<Pick<SearchParameters, 'rooms' | 'optimizeRooms'>>
    ) => {
      if (payload.rooms) {
        state.values.rooms = payload.rooms
      }
      state.values.optimizeRooms = payload.optimizeRooms ? 1 : undefined
    },
    setSearchBoxDestinationDisplayValue: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<string>
    ) => {
      state.searchBoxDestinationDisplayValue = payload
    },
    setOpenDatePickerType: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<DatePickerType>
    ) => {
      if (!payload) state.isUserChangedDates = false
      state.openDatePickerType = payload
    },
    setIsSearchBoxActive: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<boolean>
    ) => {
      state.isSearchBoxActive = payload
    },
    setSearchTrigger: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<SearchTrigger>
    ) => {
      state.searchTrigger = payload
    },
    setSuggestions: (
      state: SearchBoxStateType,
      {payload}: PayloadAction<SetSuggestionsPayload>
    ) => {
      state.suggestions = payload.suggestions
      state.suggestionsSearchValue = payload.suggestionsSearchValue
    }
  },
  extraReducers: {
    [syncStoreWithUrl.type]: (
      state: SearchBoxStateType,
      action: PayloadAction<SyncStoreWithUrlPayload>
    ) => {
      const {
        urlParams,
        isMobile,
        isSearchBoxOpenByDefault,
        dateValidationReason
      } = action.payload
      const openState = getOpenState(
        state,
        urlParams,
        isSearchBoxOpenByDefault,
        dateValidationReason,
        isMobile
      )

      const isDefaultDatesMessageVisible = getDefaultDatesMessageVisibility(
        state.isDefaultDatesMessageVisible,
        isSearchBoxOpenByDefault
      )
      const searchUsesFallbackDates = !(
        urlParams.checkIn || urlParams.checkInDistance
      ) // Added for SAPI
      const isUserSearch = urlParams?.userSearch === '1'
      const hasUserDates = isUserSearch || !searchUsesFallbackDates
      const isMetaDefaultDates = hasMetaDefaultDates(urlParams)
      // this logic should only be applied on mobile, currently limited by the exp. audience
      // when cleaning up make sure the desktop behaviour is reviewed
      const isDatesVisible =
        state.isAnchorHotelUnavailable || isMetaDefaultDates || hasUserDates

      state.openState = openState
      // The shadow must track the open state — keep them in sync.
      // A sticky `|| state.isSearchBoxActive` here previously left the overlay
      // visible after the reducer transitioned `openState` from Open* to Closed,
      // producing "overlay without form" bugs when the box was closed by any
      // path other than explicit user actions (e.g. syncing the URL after
      // back-navigation from AP).
      state.isSearchBoxActive = Boolean(
        (openState === OpenState.OpenByDefault ||
          openState === OpenState.OpenByUser) &&
          isMobile
      )

      state.isDefaultDatesMessageVisible = isDefaultDatesMessageVisible
      state.isDatesVisible = isDatesVisible
      state.ancestor = state.ancestor || DEFAULT_ANCESTOR
    },
    [userSuccessfullyAuthenticated.type]: (state: SearchBoxStateType) => {
      state.ancestor = null
      state.openState = OpenState.Closed
      state.isDefaultDatesMessageVisible = false
      state.searchTrigger = SearchTrigger.Close
      state.isSearchBoxActive = false
    },
    [anchorReceived.type]: (
      state: SearchBoxStateType,
      action: PayloadAction<AnchorReceivedPayload>
    ) => {
      const {searchParameters} = action.payload
      const values = extractSearchBoxValues(searchParameters)
      applySearchBoxValues(state, values)

      // Reset temporary destination display value when anchor is received
      state.searchBoxDestinationDisplayValue = null
    }
  }
})

export const {
  setSearchBoxValue,
  setSearchBoxValues,
  setLandingGuestConfig,
  setSearchBoxDestinationDisplayValue,
  setOpenDatePickerType,
  openSearchBox,
  closeSearchBox,
  setSuggestions,
  setSearchTrigger,
  setIsSearchBoxActive
} = actions

export default reducer
